/**
 * Post Type System - TypeScript Interfaces
 * Supporting 8 different post types with specialized handling
 * 
 * @version 1.0.0
 * @author JGS Tech Team
 */

// Supported post type enums
export enum PostType {
  NORMAL_POST = 'NORMAL_POST',
  IMAGE_POST = 'IMAGE_POST',
  VIDEO_POST = 'VIDEO_POST',
  MAGAZINE_POST = 'MAGAZINE_POST',
  CAROUSEL_POST = 'CAROUSEL_POST',
  OPINION_POLL = 'OPINION_POLL',
  ADVERTISEMENT_POST = 'ADVERTISEMENT_POST',
  PRODUCT_POST = 'PRODUCT_POST',
  FULL_TEXT_POST = 'FULL_TEXT_POST',
  SHORT_VIDEO_POST = 'SHORT_VIDEO_POST',
  PODCAST_POST = 'PODCAST_POST',
  CONTENT_CAROUSEL_POST = 'CONTENT_CAROUSEL_POST'
}

// File type enums for validation
export enum FileType {
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
  DOCUMENT = 'DOCUMENT',
  AUDIO = 'AUDIO'
}

// Post type configuration interface
export interface PostTypeConfig {
  typeName: PostType;
  allowedExtensions: string[];
  supportsMultipleSubArticles: boolean;
  requiresSpecializedStorage: boolean;
  primaryFileType: FileType;
  maxFileSize?: number; // in bytes
  displayName: string;
  description: string;
  icon?: string;
  hidden?: boolean; // When true, post type is hidden from dropdown selection
  deprecated?: boolean; // When true, post type is deprecated (kept for backwards compatibility)
  validationRules?: {
    minSubArticles?: number;
    maxSubArticles?: number;
    requiredFields?: string[];
    customValidation?: (data: any) => string | null;
  };
}

// Enhanced file upload request
export interface EnhancedFileUploadRequest {
  file?: File;
  externalUrl?: string;
  postType?: PostType;
  fileCategory?: 'file' | 'thumbnail';
  metadata?: {
    originalName?: string;
    description?: string;
    altText?: string;
    contentSource?: 'DIRECT' | 'EXTERNAL';
    platform?: string;
    originalUrl?: string;
  };
}

// Enhanced file upload response
export interface EnhancedFileUploadResponse {
  filePath: string;
  postType?: PostType;
  fileSize?: number;
  mimeType?: string;
  originalName: string;
  uploadedAt: string;
  metadata?: {
    duration?: number; // for videos
    dimensions?: {
      width: number;
      height: number;
    };
    contentSource?: 'DIRECT' | 'EXTERNAL';
    platform?: string;
    originalUrl?: string;
  };
}

// Post type validation result
export interface PostTypeValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  detectedPostType?: PostType;
}

// Opinion poll specific interfaces
export interface PollOption {
  id?: number;
  optionText: string;
  optionOrder: number;
  votes?: number;
}

export interface PollData {
  question: string;
  description?: string;
  options: PollOption[];
  allowMultipleAnswers: boolean;
  endDate?: Date;
  isAnonymous: boolean;
  pollSettings?: {
    showResults: 'after_vote' | 'after_end' | 'never';
    allowComments: boolean;
    requireAuth: boolean;
  };
}

// Advertisement specific interfaces
export interface AdData {
  title: string;
  description: string;
  advertiserName: string;
  targetUrl?: string;
  adType: 'banner' | 'video' | 'native' | 'popup';
  pricing: {
    type: 'cpm' | 'cpc' | 'fixed';
    amount: number;
    currency: string;
  };
  targeting?: {
    demographics?: string[];
    interests?: string[];
    locations?: string[];
    ageRange?: {
      min: number;
      max: number;
    };
  };
  schedule?: {
    startDate: Date;
    endDate: Date;
    timezone: string;
  };
}

// Product specific interfaces
export interface ProductData {
  name: string;
  description: string;
  price: {
    amount: number;
    currency: string;
    originalPrice?: number;
  };
  category: string;
  brand?: string;
  sku?: string;
  availability: 'in_stock' | 'out_of_stock' | 'pre_order';
  specifications?: {
    [key: string]: string | number | boolean;
  };
  shipping?: {
    weight?: number;
    dimensions?: {
      length: number;
      width: number;
      height: number;
      unit: string;
    };
    freeShipping: boolean;
  };
  seo?: {
    metaTitle?: string;
    metaDescription?: string;
    keywords?: string[];
  };
}

// Enhanced sub-article interface with post type support
export interface EnhancedSubArticle {
  id?: number;
  title: string;
  description?: string;
  body: string;
  fileUrl?: string; // Changed from imagePath to fileUrl
  thumbnailPath?: string;
  tags: string[];
  sequence?: number;
  postType?: PostType; // New field for sub-article post type
  metadata?: {
    duration?: number; // for video sub-articles
    fileSize?: number;
    mimeType?: string;
  };
}

// Enhanced article payload with post type support
export interface EnhancedArticlePayload {
  articleId?: string;
  title: string;
  description: string;
  body: string;
  tags: string[];
  fileUrl?: string; // Changed from imagePath to fileUrl for unified file handling
  thumbnailPath?: string;
  postType?: PostType; // Optional - defaults to NORMAL_POST
  articleTypeId: number;
  
  // Post-type specific data
  pollData?: PollData; // For OPINION_POLL
  adData?: AdData; // For ADVERTISEMENT_POST
  productData?: ProductData; // For PRODUCT_POST
  
  // Advertisement redirection support
  // Enterprise Feature: Enables external browser redirection for advertisements/promotional content
  redirectUrl?: string; // External URL for advertisement content redirection
  isAdvertisement?: boolean; // Flag indicating if article is an advertisement
  
  // Enhanced sub-articles with post type support
  subArticles: EnhancedSubArticle[];
  
  // Existing fields
  categories: any[];
  primaryCategory?: any;
  regionalTargeting?: any;
  targetStateIds?: number[];
  targetStateNames?: string[];
  
  // Metadata
  createdAt?: Date;
  publishedStatus?: number;
  status?: number;
  articleNumber?: number;
}

// Enhanced article response with post type information
export interface EnhancedArticleResponse extends EnhancedArticlePayload {
  id: number;
  articleId: string;
  postType: PostType;
  postTypeConfig?: PostTypeConfig;
  fileValidation?: PostTypeValidationResult;
  createdAt: Date;
  updatedAt?: Date;
  publishedAt?: Date;
  authorId?: number;
  authorName?: string;
  viewCount?: number;
  likeCount?: number;
  commentCount?: number;
  shareCount?: number;
  
  // Post-type specific response data
  pollStats?: {
    totalVotes: number;
    participantCount: number;
    endDate?: Date;
    isActive: boolean;
  };
  
  adStats?: {
    impressions: number;
    clicks: number;
    ctr: number;
    spend: number;
    isActive: boolean;
  };
  
  productStats?: {
    views: number;
    favorites: number;
    inquiries: number;
    isAvailable: boolean;
  };
}

// Auto-detection criteria for post types
export interface PostTypeDetectionCriteria {
  fileExtensions: string[];
  contentPatterns: RegExp[];
  subArticleThresholds: {
    min?: number;
    max?: number;
  };
  keywordMatches: string[];
  priority: number; // Higher priority takes precedence
}

/**
 * Result of file validation operation
 */
export interface FileValidationResult {
  isValid: boolean;
  errors: string[];
  warnings?: string[];
  fileInfo?: {
    size: number;
    type: string;
    name: string;
  };
}

// Post type service configuration
export interface PostTypeServiceConfig {
  enableAutoDetection: boolean;
  defaultPostType: PostType;
  fileUploadBaseUrl: string;
  maxFileSizes: {
    [key in PostType]: number;
  };
  allowedExtensions: {
    [key in PostType]: string[];
  };
  storagePaths: {
    [key in PostType]: string;
  };
  validation: {
    strictMode: boolean;
    validateFileTypes: boolean;
    validateSubArticleLimits: boolean;
  };
}

// Error types for post type operations
export class PostTypeError extends Error {
  constructor(
    message: string,
    public postType?: PostType,
    public validationErrors?: string[],
    public code?: string
  ) {
    super(message);
    this.name = 'PostTypeError';
  }
}

export class FileValidationError extends PostTypeError {
  constructor(
    message: string,
    public fileName: string,
    public fileType: string,
    public allowedTypes: string[]
  ) {
    super(message, undefined, undefined, 'FILE_VALIDATION_ERROR');
    this.name = 'FileValidationError';
  }
}
